import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { LIAS, permisosUsuario } from '@shared/constants/lias.constants';

@Component({
    selector     : 'modern-layout',
    templateUrl  : './modern.component.html',
    encapsulation: ViewEncapsulation.None
})
export class ModernLayoutComponent implements OnInit, OnDestroy
{
    isScreenSmall: boolean;
    isSmallScreen: boolean;
    navigation: Navigation;
    /** Cajón lateral de pantallas pequeñas, donde el menú horizontal no alcanza */
    navegacionMovil: FuseNavigationItem[] = [];
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.isSmallScreen = window.innerWidth < 768;
    }

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService
    )
    {
        this.isSmallScreen = window.innerWidth < 768;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                const horizontal = this._marcarLiasSinAcceso(navigation?.horizontal);
                this.navigation = navigation && {...navigation, horizontal};
                this.navegacionMovil = this._menuMovil(horizontal);
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Las LIAs que el usuario no tiene siguen en el menú para que sepa que existen, pero con
     * candado y llevando a su vitrina en vez del aviso de acceso restringido.
     */
    private _marcarLiasSinAcceso(items: FuseNavigationItem[]): FuseNavigationItem[]
    {
        const tiene = permisosUsuario();
        return items?.map((item) => {
            const lia = LIAS.find(l => l.link === item.link);
            if ( !lia || tiene(lia.acceso) )
            {
                return item;
            }
            return {
                ...item,
                link   : `/home/conoce/${lia.slug}`,
                icon   : 'heroicons_solid:lock-closed',
                classes: {icon: 'icon-size-4 opacity-70'},
                tooltip: 'Conoce esta LIA'
            };
        });
    }

    /**
     * Menú del cajón: Inicio y las mismas LIAs del menú superior. Las que tiene llevan su ícono;
     * las que no, el candado que ya les puso _marcarLiasSinAcceso.
     */
    private _menuMovil(lias: FuseNavigationItem[] = []): FuseNavigationItem[]
    {
        return [
            {id: 'inicio', title: 'Inicio', type: 'basic', icon: 'heroicons_outline:home', link: '/home', exactMatch: true},
            {
                id      : 'lias',
                title   : 'LIAs',
                type    : 'group',
                children: lias.map(item => item.icon
                    // Candado del mismo tamaño que los demás íconos del cajón, para que los títulos alineen
                    ? {...item, icon: 'heroicons_outline:lock-closed', classes: {icon: 'opacity-60'}}
                    : {...item, icon: LIAS.find(l => l.link === item.link)?.iconoMenu})
            }
        ];
    }
}
